import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, ValidationErrors } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/core';
import { MatError, MatFormField, MatHint, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { DynamicFormOptionConfig } from '@ng-dynamic-forms/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { WINDOW } from 'ngx-window-token';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { CustomDynamicFormControlComponent } from '../custom-dynamic-form-control.component';
import { SelectFilterModel } from './select-filter.model';

@UntilDestroy()
@Component({
  selector:    'gsm-select-filter',
  templateUrl: './select-filter.component.html',
  styleUrls:   ['./select-filter.component.scss'],
  standalone:  true,
  imports:     [
    MatFormField,
    NgIf,
    MatLabel,
    MatInput,
    FormsModule,
    MatAutocompleteTrigger,
    ReactiveFormsModule,
    MatSuffix,
    MatAutocomplete,
    NgFor,
    MatOption,
    MatHint,
    MatError,
    AsyncPipe
  ]
})
export class SelectFilterComponent extends CustomDynamicFormControlComponent<SelectFilterModel<any>> implements OnInit {
  private window = inject<Window>(WINDOW);

  selectFilterCtrl = new UntypedFormControl();
  selectFilterValues$ = this.selectFilterCtrl.valueChanges.pipe(untilDestroyed(this));
  filteredOptions$: Observable<DynamicFormOptionConfig<any>[]>;

  options: DynamicFormOptionConfig<any>[] = [];

  constructor() {
    super();

    this.filteredOptions$ = this.selectFilterValues$.pipe(
      startWith(''),
      map(text => (text ? this._filterOptions(text as any) : this.options.slice()))
    );

    this.selectFilterValues$.pipe()
      .subscribe(() => this._onSelectFilterValueChange());
  }

  ngOnInit() {
    super.ngOnInit();
  }

  protected onModelChange() {
    this.options = this.model.options || [];
    this.selectFilterCtrl.setValue(this._getOptionForValue(this.model.value));
    if (this.model.disabled) {
      this.selectFilterCtrl.disable();
    } else {
      this.selectFilterCtrl.enable();
    }

    if (this.model.additional?.immediateValidation) {
      this.selectFilterCtrl.markAsTouched();
    }
  }

  displayFn(option?: DynamicFormOptionConfig<any>) {
    return option ? option.label : undefined;
  }

  onSelectFilterBlur() {
    // setTimeout is necessary here because when an option in the dropdown is clicked, the blur event occurs before the value is set.
    this.window.setTimeout(() => {
      const value = this._getSelectFilterValue();
      if (!value) {
        this.selectFilterCtrl.setValue(null);
      }
    }, 100);
  }

  private _onSelectFilterValueChange() {
    this.model.value = this._getSelectFilterValue();
    this._setErrors();
    this.change.emit(this.model.value);
  }

  private _setErrors() {
    const errors: ValidationErrors = this.model.required && !this.model.value ? {required: true} : null;
    this.group.get(this.model.id)
      .setErrors(errors);
  }

  private _getSelectFilterValue(): any {
    let option: string | DynamicFormOptionConfig<any> = this.selectFilterCtrl.value;

    if (typeof option === 'string') {
      // if it's a string, it means the user typed in something rather than picking it from the options list
      option = this.options.find(opt => opt.label === option);
    }

    return option && option.value;
  }

  private _getOptionForValue(value: any): DynamicFormOptionConfig<any> {
    return this.options.find(opt => this.model.compareWithFn(value, opt.value));
  }

  private _filterOptions(value: string | DynamicFormOptionConfig<any>): DynamicFormOptionConfig<any>[] {
    if (value && typeof value !== 'string') {
      // if it's not a string, it's an option -> get the label
      value = value.label;
    }
    const filterValue = value && (value as string).toLowerCase();

    return this.options.filter(option => option.label.toLowerCase()
      .indexOf(filterValue) === 0);
  }
}
